import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  InputGroup,
  Alert,
  Modal,
  Badge
} from 'react-bootstrap';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink, pdf, Image } from '@react-pdf/renderer';
import {
  FaFileInvoiceDollar,
  FaFilePdf,
  FaEnvelope,
  FaSave,
  FaPlus,
  FaTrash,
  FaDollarSign,
  FaCalendarAlt,
  FaHome,
  FaUser,
  FaPrint,
  FaCheckCircle
} from 'react-icons/fa';
import axiosInstance from '../../helpers/api';
import { toast } from 'react-toastify';

interface LandlordInvoiceProps {
  property?: any;
  landlord?: any;
  transactionId?: string;
  onSave?: (invoice: any) => void;
}

interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  landlordName: string;
  landlordEmail: string;
  landlordPhone: string;
  landlordAddress: string;
  propertyAddress: string;
  leaseStartDate: string;
  leaseEndDate: string;
  tenantName: string;
  monthlyRent: number;
  commissionType: 'percentage' | 'flat' | 'monthly';
  commissionRate: number;
  commissionMonths: number;
  lineItems: InvoiceLineItem[];
  subtotal: number;
  tax: number;
  taxRate: number;
  total: number;
  notes: string;
  paymentTerms: string;
  agentName: string;
  agentEmail: string;
  agentPhone: string;
  agentLicense: string;
  brokerageName: string;
  brokerageAddress: string;
  brokeragePhone: string;
  brokerReferenceId?: string;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const LandlordInvoiceGenerator: React.FC<LandlordInvoiceProps> = ({
  property,
  landlord,
  transactionId,
  onSave
}) => {
  const [loading, setLoading] = useState(false);
  const [agentData, setAgentData] = useState<any>(null);
  const [brokerageData, setBrokerageData] = useState<any>(null);
  const [companyProfile, setCompanyProfile] = useState<any>(null);
  const [invoiceData, setInvoiceData] = useState<InvoiceData>({
    invoiceNumber: `INV-${Date.now()}`,
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days
    landlordName: landlord?.name || '',
    landlordEmail: landlord?.email || '',
    landlordPhone: landlord?.phone || '',
    landlordAddress: landlord?.address || '',
    propertyAddress: property?.address || '',
    leaseStartDate: '',
    leaseEndDate: '',
    tenantName: '',
    monthlyRent: 0,
    commissionType: 'percentage',
    commissionRate: 8, // Default 8% for first month
    commissionMonths: 1,
    lineItems: [],
    subtotal: 0,
    tax: 0,
    taxRate: 0,
    total: 0,
    notes: '',
    paymentTerms: 'Net 30',
    agentName: '',
    agentEmail: '',
    agentPhone: '',
    agentLicense: '',
    brokerageName: '',
    brokerageAddress: '',
    brokeragePhone: ''
  });
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchAgentData();
    fetchBrokerageData();
    fetchCompanyProfile();
  }, []);

  useEffect(() => {
    calculateTotals();
  }, [invoiceData.lineItems, invoiceData.taxRate]);

  useEffect(() => {
    if (transactionId) {
      fetchExistingInvoices();
    }
  }, [transactionId]);

  const fetchExistingInvoices = async () => {
    try {
      const response = await axiosInstance.get(`/invoices/landlord?transaction_id=${transactionId}`);
      
      if (response.data.success && response.data.invoices.length > 0) {
        // Sort invoices by created_at to get the most recent
        const sortedInvoices = response.data.invoices.sort((a: any, b: any) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        
        // Load the most recent invoice
        const latestInvoice = sortedInvoices[0];
        console.log('Loading latest invoice:', latestInvoice.invoice_number, 'created at:', latestInvoice.created_at);
        
        // Load line items if available
        console.log('Latest invoice line_items:', latestInvoice.line_items);
        let mappedLineItems: InvoiceLineItem[] = [];
        if (latestInvoice.line_items && latestInvoice.line_items.length > 0) {
          mappedLineItems = latestInvoice.line_items.map((item: any) => ({
            id: item.id || Date.now().toString(),
            description: item.description || '',
            quantity: parseFloat(item.quantity) || 1,
            rate: parseFloat(item.rate) || 0,
            amount: parseFloat(item.amount) || 0
          }));
          console.log('Mapped line items:', mappedLineItems);
        }
        
        // Update form with existing invoice data INCLUDING line items
        setInvoiceData(prev => ({
          ...prev,
          invoiceNumber: latestInvoice.invoice_number,
          brokerReferenceId: latestInvoice.broker_reference_id,
          invoiceDate: latestInvoice.invoice_date?.split('T')[0] || prev.invoiceDate,
          dueDate: latestInvoice.due_date?.split('T')[0] || prev.dueDate,
          landlordName: latestInvoice.landlord_name || prev.landlordName,
          landlordEmail: latestInvoice.landlord_email || prev.landlordEmail,
          landlordPhone: latestInvoice.landlord_phone || prev.landlordPhone,
          landlordAddress: latestInvoice.landlord_address || prev.landlordAddress,
          propertyAddress: latestInvoice.property_address || prev.propertyAddress,
          leaseStartDate: latestInvoice.lease_start_date?.split('T')[0] || prev.leaseStartDate,
          leaseEndDate: latestInvoice.lease_end_date?.split('T')[0] || prev.leaseEndDate,
          tenantName: latestInvoice.tenant_name || prev.tenantName,
          monthlyRent: parseFloat(latestInvoice.monthly_rent) || prev.monthlyRent,
          commissionType: latestInvoice.commission_type || prev.commissionType,
          commissionRate: parseFloat(latestInvoice.commission_rate) || prev.commissionRate,
          commissionMonths: parseInt(latestInvoice.commission_months) || prev.commissionMonths,
          taxRate: parseFloat(latestInvoice.tax_rate) || prev.taxRate,
          notes: latestInvoice.notes || prev.notes,
          paymentTerms: latestInvoice.payment_terms || prev.paymentTerms,
          lineItems: mappedLineItems // Include line items in the same update
        }));
      }
    } catch (error) {
      console.error('Error fetching existing invoices:', error);
      // Don't show error toast - it's okay if no invoices exist yet
    }
  };

  const fetchAgentData = async () => {
    try {
      setLoading(true);
      
      // Fetch real agent data
      const response = await axiosInstance.get('/agent/commission-profile');
      
      if (response.data.success) {
        const profile = response.data.data;
        
        // Fetch user details
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : null;
        
        setAgentData({
          name: profile.agentName || user?.name || 'Agent',
          email: profile.email || user?.email || '',
          phone: user?.phone || '',
          license: user?.license || '',
          brokerageName: profile.brokerageName || 'Independent Broker',
          brokerageAddress: '',
          brokeragePhone: ''
        });
        
        // Update invoice with agent data
        setInvoiceData(prev => ({
          ...prev,
          agentName: profile.agentName || user?.name || 'Agent',
          agentEmail: profile.email || user?.email || '',
          agentPhone: user?.phone || '',
          agentLicense: user?.license || '',
          brokerageName: profile.brokerageName || 'Independent Broker'
        }));
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching agent data:', error);
      setLoading(false);
    }
  };

  const fetchBrokerageData = async () => {
    try {
      const response = await axiosInstance.get('/invoices/brokerage-info');
      
      if (response.data.success && response.data.brokerage) {
        const brokerage = response.data.brokerage;
        setBrokerageData(brokerage);
        
        // Update invoice data with brokerage info
        setInvoiceData(prev => ({
          ...prev,
          brokerageName: brokerage.brokerage_name || prev.brokerageName,
          brokerageAddress: `${brokerage.address || ''}, ${brokerage.city || ''}, ${brokerage.state || ''} ${brokerage.zip_code || ''}`.trim(),
          brokeragePhone: brokerage.phone || prev.brokeragePhone
        }));
      }
    } catch (error) {
      console.error('Error fetching brokerage data:', error);
      // Don't show error toast - brokerage data is optional
    }
  };

  const fetchCompanyProfile = async () => {
    try {
      console.log('Fetching company profile...');
      const response = await axiosInstance.get('/broker/company-profile');
      console.log('Company profile response:', response.data);
      
      if (response.data.success && response.data.profile) {
        const profile = response.data.profile;
        setCompanyProfile(profile);
        console.log('Company profile loaded:', profile);
        
        // Update invoice data with company info
        setInvoiceData(prev => ({
          ...prev,
          brokerageName: profile.company_name || 'KairosIQ AI Realty, Inc.',
          brokerageAddress: profile.company_address || '2700 S Gilbert Rd Ste 5, Chandler, AZ 85286',
          brokeragePhone: profile.company_phone || prev.brokeragePhone
        }));
      } else {
        console.log('No profile data found, using defaults');
        // Use defaults if no profile data
        setInvoiceData(prev => ({
          ...prev,
          brokerageName: 'KairosIQ AI Realty, Inc.',
          brokerageAddress: '2700 S Gilbert Rd Ste 5, Chandler, AZ 85286'
        }));
      }
    } catch (error: any) {
      console.error('Error fetching company profile:', error.response?.data || error.message);
      // Use correct defaults if company profile not available
      setInvoiceData(prev => ({
        ...prev,
        brokerageName: 'KairosIQ AI Realty, Inc.',
        brokerageAddress: '2700 S Gilbert Rd Ste 5, Chandler, AZ 85286'
      }));
    }
  };

  const calculateCommission = () => {
    const { monthlyRent, commissionType, commissionRate, commissionMonths } = invoiceData;
    
    if (commissionType === 'percentage') {
      // Percentage of rent (e.g., 8% of monthly rent)
      return (monthlyRent * commissionRate / 100) * commissionMonths;
    } else if (commissionType === 'flat') {
      // Flat fee
      return commissionRate;
    } else {
      // Monthly management fee
      return commissionRate * commissionMonths;
    }
  };

  const addStandardCommissionItem = () => {
    const commission = calculateCommission();
    const description = invoiceData.commissionType === 'percentage' 
      ? `Leasing Commission - ${invoiceData.commissionRate}% of ${invoiceData.commissionMonths} month(s) rent`
      : invoiceData.commissionType === 'flat'
      ? 'Leasing Commission - Flat Fee'
      : `Property Management Fee - ${invoiceData.commissionMonths} month(s)`;
    
    const newItem: InvoiceLineItem = {
      id: Date.now().toString(),
      description,
      quantity: 1,
      rate: commission,
      amount: commission
    };
    
    setInvoiceData(prev => ({
      ...prev,
      lineItems: [newItem]
    }));
  };

  const addLineItem = () => {
    const newItem: InvoiceLineItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0
    };
    
    setInvoiceData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, newItem]
    }));
  };

  const updateLineItem = (id: string, field: keyof InvoiceLineItem, value: any) => {
    setInvoiceData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map(item => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === 'quantity' || field === 'rate') {
            updated.amount = updated.quantity * updated.rate;
          }
          return updated;
        }
        return item;
      })
    }));
  };

  const removeLineItem = (id: string) => {
    setInvoiceData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter(item => item.id !== id)
    }));
  };

  const calculateTotals = () => {
    const subtotal = invoiceData.lineItems.reduce((sum, item) => sum + item.amount, 0);
    const tax = subtotal * (invoiceData.taxRate / 100);
    const total = subtotal + tax;
    
    setInvoiceData(prev => ({
      ...prev,
      subtotal,
      tax,
      total
    }));
  };

  const generatePDF = async () => {
    try {
      const pdfDoc = <InvoicePDF invoice={invoiceData} brokerage={brokerageData} />;
      const blob = await pdf(pdfDoc).toBlob();
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoiceData.invoiceNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Invoice PDF generated successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const sendInvoice = async () => {
    try {
      // TODO: Implement email sending
      toast.info('Email functionality coming soon');
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error('Failed to send invoice');
    }
  };

  const saveInvoice = async () => {
    try {
      setLoading(true);
      
      // Prepare data for API
      const apiData = {
        transaction_id: transactionId,
        landlord_name: invoiceData.landlordName,
        landlord_email: invoiceData.landlordEmail,
        landlord_phone: invoiceData.landlordPhone,
        landlord_address: invoiceData.landlordAddress,
        property_address: invoiceData.propertyAddress,
        lease_start_date: invoiceData.leaseStartDate,
        lease_end_date: invoiceData.leaseEndDate,
        tenant_name: invoiceData.tenantName,
        monthly_rent: invoiceData.monthlyRent,
        commission_type: invoiceData.commissionType,
        commission_rate: invoiceData.commissionRate,
        commission_months: invoiceData.commissionMonths,
        line_items: invoiceData.lineItems.map(item => ({
          description: item.description,
          quantity: item.quantity,
          rate: item.rate,
          amount: item.amount
        })),
        tax_rate: invoiceData.taxRate,
        notes: invoiceData.notes,
        payment_terms: invoiceData.paymentTerms,
        broker_reference_id: invoiceData.brokerReferenceId,
        // Commission tracking data - use default splits for now
        agent_split_percentage: 80,
        broker_split_percentage: 20,
        transaction_fee: 50,
        eo_fee: 60
      };
      
      const response = await axiosInstance.post('/invoices/landlord', apiData);
      
      if (response.data.success) {
        // Update invoice data with server-generated values
        const savedInvoice = response.data.invoice;
        setInvoiceData(prev => ({
          ...prev,
          invoiceNumber: savedInvoice.invoice_number,
          brokerReferenceId: savedInvoice.broker_reference_id
        }));
        
        toast.success(`Invoice ${savedInvoice.invoice_number} saved successfully`);
        
        // Refresh to load the saved invoice with all line items
        if (transactionId) {
          setTimeout(() => {
            fetchExistingInvoices();
          }, 500);
        }
        
        if (onSave) {
          onSave(savedInvoice);
        }
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error('Failed to save invoice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="landlord-invoice-generator">
      {/* Header */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0">
            <FaFileInvoiceDollar className="me-2" />
            Landlord Invoice Generator
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Invoice Number</Form.Label>
                <Form.Control
                  type="text"
                  value={invoiceData.invoiceNumber}
                  onChange={(e) => setInvoiceData({ ...invoiceData, invoiceNumber: e.target.value })}
                  disabled
                  title="Generated automatically"
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Broker Ref ID</Form.Label>
                <Form.Control
                  type="text"
                  value={invoiceData.brokerReferenceId || ''}
                  onChange={(e) => setInvoiceData({ ...invoiceData, brokerReferenceId: e.target.value })}
                  placeholder="REF-YYYYMMDD"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Invoice Date</Form.Label>
                <Form.Control
                  type="date"
                  value={invoiceData.invoiceDate}
                  onChange={(e) => setInvoiceData({ ...invoiceData, invoiceDate: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Due Date</Form.Label>
                <Form.Control
                  type="date"
                  value={invoiceData.dueDate}
                  onChange={(e) => setInvoiceData({ ...invoiceData, dueDate: e.target.value })}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Landlord Information */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-light">
          <h6 className="mb-0">
            <FaUser className="me-2" />
            Landlord Information
          </h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Landlord Name</Form.Label>
                <Form.Control
                  type="text"
                  value={invoiceData.landlordName}
                  onChange={(e) => setInvoiceData({ ...invoiceData, landlordName: e.target.value })}
                  placeholder="Enter landlord name"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={invoiceData.landlordEmail}
                  onChange={(e) => setInvoiceData({ ...invoiceData, landlordEmail: e.target.value })}
                  placeholder="landlord@email.com"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="tel"
                  value={invoiceData.landlordPhone}
                  onChange={(e) => setInvoiceData({ ...invoiceData, landlordPhone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  value={invoiceData.landlordAddress}
                  onChange={(e) => setInvoiceData({ ...invoiceData, landlordAddress: e.target.value })}
                  placeholder="123 Main St, City, State ZIP"
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Property & Lease Information */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-light">
          <h6 className="mb-0">
            <FaHome className="me-2" />
            Property & Lease Information
          </h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Property Address</Form.Label>
                <Form.Control
                  type="text"
                  value={invoiceData.propertyAddress}
                  onChange={(e) => setInvoiceData({ ...invoiceData, propertyAddress: e.target.value })}
                  placeholder="Enter property address"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Lease Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={invoiceData.leaseStartDate}
                  onChange={(e) => setInvoiceData({ ...invoiceData, leaseStartDate: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Lease End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={invoiceData.leaseEndDate}
                  onChange={(e) => setInvoiceData({ ...invoiceData, leaseEndDate: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Tenant Name</Form.Label>
                <Form.Control
                  type="text"
                  value={invoiceData.tenantName}
                  onChange={(e) => setInvoiceData({ ...invoiceData, tenantName: e.target.value })}
                  placeholder="Tenant name"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Monthly Rent</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type="number"
                    value={invoiceData.monthlyRent}
                    onChange={(e) => setInvoiceData({ ...invoiceData, monthlyRent: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    step="0.01"
                  />
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          {/* Commission Calculation */}
          <hr />
          <h6 className="mb-3">Commission Calculation</h6>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Commission Type</Form.Label>
                <Form.Select
                  value={invoiceData.commissionType}
                  onChange={(e) => setInvoiceData({ ...invoiceData, commissionType: e.target.value as any })}
                >
                  <option value="percentage">Percentage of Rent</option>
                  <option value="flat">Flat Fee</option>
                  <option value="monthly">Monthly Management</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>
                  {invoiceData.commissionType === 'percentage' ? 'Commission %' : 'Fee Amount'}
                </Form.Label>
                <InputGroup>
                  {invoiceData.commissionType !== 'percentage' && <InputGroup.Text>$</InputGroup.Text>}
                  <Form.Control
                    type="number"
                    value={invoiceData.commissionRate}
                    onChange={(e) => setInvoiceData({ ...invoiceData, commissionRate: parseFloat(e.target.value) || 0 })}
                    step={invoiceData.commissionType === 'percentage' ? "0.1" : "0.01"}
                  />
                  {invoiceData.commissionType === 'percentage' && <InputGroup.Text>%</InputGroup.Text>}
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Number of Months</Form.Label>
                <Form.Control
                  type="number"
                  value={invoiceData.commissionMonths}
                  onChange={(e) => setInvoiceData({ ...invoiceData, commissionMonths: parseInt(e.target.value) || 1 })}
                  min="1"
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button
                variant="primary"
                className="w-100 mb-3"
                onClick={addStandardCommissionItem}
              >
                <FaPlus className="me-2" />
                Add Commission
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Line Items */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-light d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <FaDollarSign className="me-2" />
            Invoice Items
          </h6>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={addLineItem}
          >
            <FaPlus className="me-1" />
            Add Item
          </Button>
        </Card.Header>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Description</th>
                <th style={{width: "100px"}}>Qty</th>
                <th style={{width: "150px"}}>Rate</th>
                <th style={{width: "150px"}}>Amount</th>
                <th style={{width: "50px"}}></th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.lineItems.map(item => (
                <tr key={item.id}>
                  <td>
                    <Form.Control
                      type="text"
                      value={item.description}
                      onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                      placeholder="Item description"
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                      min="0"
                      step="1"
                    />
                  </td>
                  <td>
                    <InputGroup>
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control
                        type="number"
                        value={item.rate}
                        onChange={(e) => updateLineItem(item.id, 'rate', parseFloat(e.target.value) || 0)}
                        step="0.01"
                      />
                    </InputGroup>
                  </td>
                  <td className="text-end align-middle">
                    {formatCurrency(item.amount)}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="link"
                      className="text-danger p-0"
                      onClick={() => removeLineItem(item.id)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
              {invoiceData.lineItems.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    No items added yet. Add commission or custom items above.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="text-end"><strong>Subtotal:</strong></td>
                <td className="text-end">{formatCurrency(invoiceData.subtotal)}</td>
                <td></td>
              </tr>
              <tr>
                <td colSpan={2} className="text-end"><strong>Tax:</strong></td>
                <td>
                  <InputGroup size="sm">
                    <Form.Control
                      type="number"
                      value={invoiceData.taxRate}
                      onChange={(e) => setInvoiceData({ ...invoiceData, taxRate: parseFloat(e.target.value) || 0 })}
                      step="0.01"
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </td>
                <td className="text-end">{formatCurrency(invoiceData.tax)}</td>
                <td></td>
              </tr>
              <tr className="table-primary">
                <td colSpan={3} className="text-end"><strong>Total Due:</strong></td>
                <td className="text-end"><strong>{formatCurrency(invoiceData.total)}</strong></td>
                <td></td>
              </tr>
            </tfoot>
          </Table>
        </Card.Body>
      </Card>

      {/* Additional Information */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-light">
          <h6 className="mb-0">Additional Information</h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Payment Terms</Form.Label>
                <Form.Select
                  value={invoiceData.paymentTerms}
                  onChange={(e) => setInvoiceData({ ...invoiceData, paymentTerms: e.target.value })}
                >
                  <option value="Due on Receipt">Due on Receipt</option>
                  <option value="Net 15">Net 15</option>
                  <option value="Net 30">Net 30</option>
                  <option value="Net 45">Net 45</option>
                  <option value="Net 60">Net 60</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={invoiceData.notes}
                  onChange={(e) => setInvoiceData({ ...invoiceData, notes: e.target.value })}
                  placeholder="Additional notes or payment instructions..."
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Actions */}
      <Card className="shadow-sm">
        <Card.Body>
          <Row>
            <Col className="d-flex gap-2 justify-content-end">
              <Button
                variant="outline-secondary"
                onClick={() => setShowPreview(true)}
              >
                <FaFilePdf className="me-2" />
                Preview
              </Button>
              <PDFDownloadLink
                document={<InvoicePDF invoice={invoiceData} brokerage={brokerageData} />}
                fileName={`Invoice_${invoiceData.invoiceNumber}.pdf`}
                className="btn btn-primary"
              >
                {({ loading }) => (
                  loading ? 'Generating...' : (
                    <>
                      <FaFilePdf className="me-2" />
                      Download PDF
                    </>
                  )
                )}
              </PDFDownloadLink>
              <Button
                variant="info"
                onClick={sendInvoice}
              >
                <FaEnvelope className="me-2" />
                Send Email
              </Button>
              <Button
                variant="success"
                onClick={saveInvoice}
                disabled={loading}
              >
                <FaSave className="me-2" />
                {loading ? 'Saving...' : 'Save Invoice'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Preview Modal */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Invoice Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Simple preview - in production, use react-pdf viewer */}
          <div className="p-4 border">
            <div className="d-flex justify-content-between align-items-start mb-4">
              {brokerageData?.logo_url && (
                <img 
                  src={brokerageData.logo_url} 
                  alt="Brokerage Logo" 
                  style={{ maxHeight: '60px', maxWidth: '150px', objectFit: 'contain' }}
                />
              )}
              <div className="text-end">
                <h4>Invoice #{invoiceData.invoiceNumber}</h4>
                {invoiceData.brokerReferenceId && (
                  <p className="mb-0"><small>Ref: {invoiceData.brokerReferenceId}</small></p>
                )}
                <p className="mb-0">Date: {formatDate(invoiceData.invoiceDate)}</p>
                <p className="mb-0">Due: {formatDate(invoiceData.dueDate)}</p>
              </div>
            </div>
            <hr />
            <h5>Bill To:</h5>
            <p>{invoiceData.landlordName}<br />
            {invoiceData.landlordAddress}</p>
            <hr />
            <h5>Property: {invoiceData.propertyAddress}</h5>
            <p>Lease: {formatDate(invoiceData.leaseStartDate)} - {formatDate(invoiceData.leaseEndDate)}</p>
            <hr />
            <h5>Total Due: {formatCurrency(invoiceData.total)}</h5>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

// PDF Document Component
const InvoicePDF: React.FC<{ invoice: InvoiceData; brokerage?: any }> = ({ invoice, brokerage }) => {
  const styles = StyleSheet.create({
    page: {
      padding: 30,
      fontSize: 10,
      fontFamily: 'Helvetica'
    },
    header: {
      marginBottom: 20,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start'
    },
    logoContainer: {
      width: 120,
      marginRight: 20
    },
    logo: {
      maxWidth: 120,
      maxHeight: 60,
      objectFit: 'contain'
    },
    headerInfo: {
      flex: 1
    },
    title: {
      fontSize: 24,
      fontWeight: 'bold',
      marginBottom: 10
    },
    row: {
      flexDirection: 'row',
      marginBottom: 5
    },
    col: {
      flex: 1
    },
    label: {
      fontWeight: 'bold',
      marginBottom: 2
    },
    section: {
      marginBottom: 20
    },
    table: {
      marginTop: 10
    },
    tableHeader: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: '#000',
      paddingBottom: 5,
      marginBottom: 5
    },
    tableRow: {
      flexDirection: 'row',
      paddingVertical: 5,
      borderBottomWidth: 0.5,
      borderBottomColor: '#ccc'
    },
    tableCell: {
      flex: 1
    },
    totalRow: {
      flexDirection: 'row',
      marginTop: 10,
      paddingTop: 10,
      borderTopWidth: 1,
      borderTopColor: '#000'
    },
    footer: {
      position: 'absolute',
      bottom: 30,
      left: 30,
      right: 30,
      textAlign: 'center',
      fontSize: 8,
      color: '#666'
    }
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          {/* KairosIQ Logo */}
          <View style={styles.logoContainer}>
            <Image 
              style={styles.logo} 
              src="https://dev.agentkairos.com/public/assets/kairosiq-logo.png"
            />
          </View>
          
          {/* Header Info */}
          <View style={styles.headerInfo}>
            <Text style={styles.title}>INVOICE</Text>
            <View style={styles.row}>
              <View style={styles.col}>
                <Text style={styles.label}>Invoice #:</Text>
                <Text>{invoice.invoiceNumber}</Text>
              </View>
              <View style={styles.col}>
                <Text style={styles.label}>Date:</Text>
                <Text>{formatDate(invoice.invoiceDate)}</Text>
              </View>
              <View style={styles.col}>
                <Text style={styles.label}>Due Date:</Text>
                <Text>{formatDate(invoice.dueDate)}</Text>
              </View>
            </View>
            {invoice.brokerReferenceId && (
              <View style={styles.row}>
                <Text style={styles.label}>Reference ID: </Text>
                <Text>{invoice.brokerReferenceId}</Text>
              </View>
            )}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.label}>From:</Text>
          <Text>{invoice.brokerageName || 'KairosIQ AI Realty, Inc.'}</Text>
          <Text>{invoice.brokerageAddress || '2700 S Gilbert Rd Ste 5, Chandler, AZ 85286'}</Text>
          {invoice.brokeragePhone && <Text>{invoice.brokeragePhone}</Text>}
          <Text>{invoice.agentName}</Text>
          <Text>{invoice.agentEmail}</Text>
          {invoice.agentPhone && <Text>{invoice.agentPhone}</Text>}
          {invoice.agentLicense && <Text>License: {invoice.agentLicense}</Text>}
        </View>

        <View style={styles.section}>
          <Text style={styles.label}>Bill To:</Text>
          <Text>{invoice.landlordName}</Text>
          <Text>{invoice.landlordAddress}</Text>
          <Text>{invoice.landlordEmail}</Text>
          <Text>{invoice.landlordPhone}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.label}>Property Information:</Text>
          <Text>Address: {invoice.propertyAddress}</Text>
          <Text>Tenant: {invoice.tenantName}</Text>
          <Text>Lease Period: {formatDate(invoice.leaseStartDate)} - {formatDate(invoice.leaseEndDate)}</Text>
          <Text>Monthly Rent: {formatCurrency(invoice.monthlyRent)}</Text>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCell, { flex: 3 }]}>Description</Text>
            <Text style={styles.tableCell}>Qty</Text>
            <Text style={styles.tableCell}>Rate</Text>
            <Text style={styles.tableCell}>Amount</Text>
          </View>
          
          {invoice.lineItems.map((item, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={[styles.tableCell, { flex: 3 }]}>{item.description}</Text>
              <Text style={styles.tableCell}>{item.quantity}</Text>
              <Text style={styles.tableCell}>{formatCurrency(item.rate)}</Text>
              <Text style={styles.tableCell}>{formatCurrency(item.amount)}</Text>
            </View>
          ))}
          
          <View style={styles.totalRow}>
            <Text style={[styles.tableCell, { flex: 3 }]}>Subtotal</Text>
            <Text style={styles.tableCell}></Text>
            <Text style={styles.tableCell}></Text>
            <Text style={styles.tableCell}>{formatCurrency(invoice.subtotal)}</Text>
          </View>
          
          {invoice.tax > 0 && (
            <View style={styles.row}>
              <Text style={[styles.tableCell, { flex: 3 }]}>Tax ({invoice.taxRate}%)</Text>
              <Text style={styles.tableCell}></Text>
              <Text style={styles.tableCell}></Text>
              <Text style={styles.tableCell}>{formatCurrency(invoice.tax)}</Text>
            </View>
          )}
          
          <View style={[styles.totalRow, { borderTopWidth: 2 }]}>
            <Text style={[styles.tableCell, { flex: 3, fontWeight: 'bold' }]}>Total Due</Text>
            <Text style={styles.tableCell}></Text>
            <Text style={styles.tableCell}></Text>
            <Text style={[styles.tableCell, { fontWeight: 'bold' }]}>{formatCurrency(invoice.total)}</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.label}>Payment Terms:</Text>
          <Text>{invoice.paymentTerms}</Text>
        </View>

        {invoice.notes && (
          <View style={styles.section}>
            <Text style={styles.label}>Notes:</Text>
            <Text>{invoice.notes}</Text>
          </View>
        )}

        {/* Payment Section */}
        <View style={[styles.section, { marginTop: 30 }]}>
          <Text style={[styles.label, { fontSize: 12, marginBottom: 10 }]}>Payment Options:</Text>
          
          <View style={styles.row}>
            <View style={[styles.col, { flex: 2 }]}>
              <Text style={{ marginBottom: 5 }}>1. Check payable to: {invoice.brokerageName}</Text>
              <Text style={{ marginBottom: 5 }}>2. Wire Transfer (contact for details)</Text>
              <Text style={{ marginBottom: 5 }}>3. Zelle Quick Pay:</Text>
              <Text style={{ paddingLeft: 15, fontSize: 9 }}>
                • Scan QR code or{'\n'}
                • Send to: {invoice.agentEmail}
              </Text>
            </View>
            
            {/* Zelle QR Code */}
            <View style={[styles.col, { flex: 1, alignItems: 'center' }]}>
              <Image 
                style={{ width: 80, height: 80 }}
                src="https://dev.agentkairos.com/public/assets/kairosiq-zelle-qr.png"
              />
              <Text style={{ fontSize: 8, marginTop: 5, fontWeight: 'bold' }}>
                KairosIQ AI Realty, Inc
              </Text>
              <Text style={{ fontSize: 7 }}>Scan to Pay</Text>
            </View>
          </View>
        </View>
        
        <Text style={styles.footer}>
          Thank you for your business!
        </Text>
      </Page>
    </Document>
  );
};

export default LandlordInvoiceGenerator;